import type {
  EnhancedResource,
  SubscriptionDetails,
  TeamListItem,
  PaywallStateManagerContextValue,
  UnifiedSubscription,
  UnifiedResource,
  UnifiedTeam,
  UnifiedPaywallState,
} from "@trello-powerup-monorepo/iframe-messenger-paywall-v2/parent";

import { SECURITY_STEPS } from "@trello-powerup-monorepo/iframe-messenger-paywall-v2/parent";

const normalizeSubscription = (
  subscription: UnifiedSubscription
): SubscriptionDetails => ({
  id: subscription.id,
  planName: subscription.planName,
  mode: subscription.mode as SubscriptionDetails["mode"],
  planId: subscription.planId,
  quantity: subscription.quantity,
  target: subscription.target as SubscriptionDetails["target"],
  variant: subscription.variant as SubscriptionDetails["variant"],
  profileId: subscription.profileId,
  teamId: subscription.teamId,
  resources: subscription.resources,
});

const normalizeResource = (
  resource: UnifiedResource
): EnhancedResource => ({
  resource: resource.resource,
  hasAccess: resource.hasAccess,
  grantingSubscriptions: resource.grantingSubscriptions.map(
    normalizeSubscription
  ),
});

const normalizeTeam = (team: UnifiedTeam): TeamListItem => ({ ...team });

export function unifiedToParentState(
  state: UnifiedPaywallState
): PaywallStateManagerContextValue {
  const resources = state.resources.map(normalizeResource);

  const securityStep = (() => {
    switch (state.authStatus) {
      case "unauthenticated":
        return SECURITY_STEPS.AUTH_FAILED;
      case "pre-authenticated":
        return SECURITY_STEPS.PROFILE_NEEDED;
      case "authenticated":
      default:
        return state.hasAccess ? SECURITY_STEPS.AUTHORIZED : SECURITY_STEPS.NO_ACCESS;
    }
  })();

  return {
    authStatus: state.authStatus,
    subscriptions: state.subscriptions.map(normalizeSubscription),
    resources,
    ownedTeams: state.ownedTeams.map(normalizeTeam),
    securityStep,
    hasAccess: state.hasAccess,
  };
}

export function parentToUnifiedState(
  state: PaywallStateManagerContextValue
): UnifiedPaywallState {
  const normalize = (
    subscription: SubscriptionDetails
  ): UnifiedSubscription => ({
    id: subscription.id,
    planName: subscription.planName,
    mode: subscription.mode,
    planId: subscription.planId,
    quantity: subscription.quantity,
    target: subscription.target,
    variant: subscription.variant,
    profileId: subscription.profileId,
    teamId: subscription.teamId,
    resources: subscription.resources,
  });

  return {
    authStatus: state.authStatus,
    subscriptions: state.subscriptions.map(normalize),
    resources: state.resources.map((resource) => ({
      resource: resource.resource,
      hasAccess: resource.hasAccess,
      grantingSubscriptions: resource.grantingSubscriptions.map(normalize),
    })),
    ownedTeams: state.ownedTeams.map((team) => ({ ...team })),
    hasAccess: state.hasAccess,
  };
}
